import { displayValue } from '../../lib/oem-display';
import { useState, useEffect, useCallback } from 'react';
import { ArrowRight, Check, ChevronRight, Clock, FileText, ZoomIn, UserRound } from 'lucide-react';
import { fileBlob } from '../../lib/oem-client';
import { runtimeMode } from '../../lib/auth';
import type { OemRecord, OemWorkspace } from '../../lib/oem-client';
export function ArtworkReview({
  records,
  workspace,
  exactRecordId,
  onAnnotate,
  onDecision,
  busy,
  canDecide,
}: {
  readonly records: OemRecord[];
  readonly workspace: OemWorkspace;
  readonly exactRecordId?: string | undefined;
  readonly onAnnotate: (record: OemRecord, x: number, y: number, comment: string) => Promise<void>;
  readonly onDecision: (record: OemRecord) => Promise<void>;
  readonly busy: boolean;
  readonly canDecide: boolean;
}) {
  const proofs = records.filter((r) => r.kind === 'artwork');
  const [selected, setSelected] = useState(exactRecordId ?? proofs.at(-1)?.id);
  const [compare, setCompare] = useState(false);
  const [scale, setScale] = useState(1);
  const [pin, setPin] = useState<{
    x: number;
    y: number;
    recordId: string;
  } | null>(null);
  const [comment, setComment] = useState('');
  const proof = proofs.find((p) => p.id === selected);
  const annotationProof = proofs.find((p) => p.id === pin?.recordId);
  const version = proof?.version ?? 0;
  const [ready, setReady] = useState<Record<string, boolean>>({});
  const fileReady = useCallback(
    (id: string, value: boolean) =>
      setReady((old) => (old[id] === value ? old : { ...old, [id]: value })),
    [],
  );
  const status = (action: string) => {
    const d = workspace.decisions
      .filter((d) => d.recordId === proof?.id && d.action === action)
      .sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt))[0];
    return d
      ? ((
          {
            passed: 'ผ่านการทบทวน',
            accepted: 'รับเฉพาะขอบเขต',
            partial: 'รับบางขอบเขต',
            failed: 'ต้องทบทวนใหม่',
            pending: 'รอผลทบทวน',
          } as Record<string, string>
        )[d.disposition ?? ''] ?? 'มีผลบันทึก · ดูรายละเอียด')
      : 'ยังไม่มีผลในเวอร์ชันนี้';
  };
  return (
    <>
      <div className="crumb">
        โครงการ <ChevronRight /> {workspace.project.brand || 'ยังไม่ระบุแบรนด์'} <ChevronRight />{' '}
        รายละเอียดฉบับที่เลือก
      </div>
      <div className="page-title">
        <div>
          <div className="eyebrow">ตรวจแบบฉลากและบรรจุภัณฑ์</div>
          <h1>ตรวจแบบก่อนส่งต่อ</h1>
          <p className="muted wrap">
            {runtimeMode === 'fixture'
              ? 'Mali Studio · OEM-DEMO-001 · บริษัท มะลิ สตูดิโอ ตัวอย่าง จำกัด'
              : 'ตรวจไฟล์และเวอร์ชันที่ได้รับสิทธิ์ในโปรเจกต์นี้'}
          </p>
        </div>
        <span className="chip amber">
          <Clock />
          ฉบับที่ {version} · {runtimeMode === 'fixture' ? 'รอตรวจ' : status('decide_design')}
        </span>
      </div>
      <div className="card action-card">
        <div className="action-icon">
          <FileText />
        </div>
        <div className="action-text">
          <h2>ตรวจเฉพาะงานออกแบบที่เห็นในเวอร์ชันนี้</h2>
          <p>
            {runtimeMode === 'fixture'
              ? 'SKU-P30 · ART-P30'
              : displayValue(proof?.content.label ?? 'ยังไม่มีแบบฉลาก')}{' '}
            ฉบับที่ {version} · หน้า {displayValue(proof?.content.proofPage ?? 1)} · รหัสไฟล์:{' '}
            {displayValue(
              proof?.content.fileId ??
                (runtimeMode === 'fixture' ? 'ภาพประกอบตัวอย่าง' : 'ยังไม่มีไฟล์'),
            )}
          </p>
        </div>
      </div>
      <div className="grid2">
        <section>
          <div className="card">
            <div className="between">
              <strong>
                {runtimeMode === 'fixture'
                  ? 'ART-P30'
                  : displayValue(proof?.content.label ?? 'ยังไม่ทราบ')}{' '}
                · ฉบับที่ {version}
              </strong>
              <span className="chip">
                {runtimeMode === 'connected'
                  ? proof?.content.fileId
                    ? 'ไฟล์ฉบับที่เลือก · รอโหลดไฟล์'
                    : 'ยังไม่มีไฟล์'
                  : 'ภาพร่างตัวอย่าง'}
              </span>
            </div>
            <div className="toolbar">
              <label>
                เวอร์ชัน
                <select
                  value={selected ?? ''}
                  onChange={(e) => {
                    setSelected(e.target.value);
                    setPin(null);
                    setComment('');
                  }}
                >
                  {proofs.map((p) => (
                    <option key={p.id} value={p.id}>
                      ART-P30 ฉบับที่ {p.version}
                    </option>
                  ))}
                </select>
              </label>
              <button className="action secondary" onClick={() => setCompare(!compare)}>
                {compare ? 'ดูเวอร์ชันเดียว' : 'เทียบเวอร์ชัน'}
              </button>
              <button className="action secondary" onClick={() => setScale(scale === 1 ? 1.4 : 1)}>
                <ZoomIn />
                {scale === 1 ? 'ขยาย' : 'ขนาดปกติ'}
              </button>
            </div>
            <div className={compare ? 'grid-equal' : ''}>
              {(compare && proof
                ? proofs
                    .filter((p) => p.rootId === proof.rootId && p.version <= proof.version)
                    .sort((a, b) => a.version - b.version)
                    .slice(-2)
                : proof
                  ? [proof]
                  : []
              ).map((p) => (
                <div
                  key={p.id}
                  className="art-canvas"
                  role="button"
                  tabIndex={0}
                  aria-label="เลือกจุดหมายเหตุบน proof"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') setPin({ x: 0.5, y: 0.5, recordId: p.id });
                  }}
                  onClick={(e) => {
                    const rect = (
                      e.currentTarget.querySelector('.art-sheet') ?? e.currentTarget
                    ).getBoundingClientRect();
                    setPin({
                      recordId: p.id,
                      x: Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width)),
                      y: Math.max(0, Math.min(1, (e.clientY - rect.top) / rect.height)),
                    });
                  }}
                >
                  {runtimeMode === 'connected' ? (
                    <ExactProofMedia key={p.id} proof={p} scale={scale} onReady={fileReady} />
                  ) : (
                    <div className="art-sheet" style={{ transform: `scale(${scale})` }}>
                      <div className="art-brand">
                        MALI
                        <br />
                        STUDIO
                      </div>
                      <div className="art-rule" />
                      <div className="sub">CREAM CONCEPT</div>
                      <p style={{ fontSize: 12, marginTop: 20 }}>30 mL</p>
                      <div className="drafttext">
                        ข้อความร่าง ยังไม่ใช่ฉลากพร้อมพิมพ์
                        <br />
                        Ingredient declaration pending review
                        <br />
                        DEMO ARTWORK · NOT FOR PRINT
                      </div>
                    </div>
                  )}
                  <span className="pin one">1</span>
                  <span className="pin two">2</span>
                  {pin?.recordId === p.id ? (
                    <span
                      className="pin"
                      style={{ left: `${pin.x * 100}%`, top: `${pin.y * 100}%` }}
                    >
                      3
                    </span>
                  ) : null}
                  <span className="proof-version">
                    v{p.version} · หน้า {displayValue(p.content.proofPage ?? 1)}
                  </span>
                </div>
              ))}
            </div>
            <p className="caption">
              หน้าแสดงแบบร่าง · ไม่มี dieline หรือขนาดที่ยืนยันแล้ว · ไม่ใช่หลักฐาน print proof
            </p>
            <div className="progress-line">
              <span>ฉบับที่ 1 · ประวัติเดิมคงอยู่</span>
              <ArrowRight />
              <strong>ฉบับที่ {version} · รอตรวจใหม่</strong>
            </div>
          </div>
        </section>
        <section className="stack">
          <div className="card">
            <h2>สามขอบเขต แยกการตัดสินใจ</h2>
            <div className="statusline">
              <span>ลูกค้า · งานออกแบบ</span>
              <span className="chip amber">
                {runtimeMode === 'fixture' ? 'รอความคิดเห็น' : status('decide_design')}
              </span>
            </div>
            <div className="statusline">
              <span>ภายใน · technical review</span>
              <span className="chip amber">
                {runtimeMode === 'fixture' ? 'รอข้อมูล' : status('review_technical')}
              </span>
            </div>
            <div className="statusline">
              <span>ภายใน · regulatory review</span>
              <span className="chip red">
                {runtimeMode === 'fixture' ? 'ยังไม่เคลียร์' : status('review_regulatory')}
              </span>
            </div>
          </div>
          <div className="card">
            <div className="audience">
              <UserRound /> ลูกค้าเห็นข้อความนี้ · exact ฉบับที่ {version}
            </div>
            <div className="issue">
              <strong>1 · ข้อความส่วนผสม</strong>
              <p>รอ ingredient declaration จากข้อมูลที่เพียงพอ และผู้ตรวจที่มีหน้าที่รับผิดชอบ</p>
            </div>
            <div className="issue">
              <strong>2 · พื้นที่ batch / date</strong>
              <p>ต้องตรวจขนาดและตำแหน่งจาก dieline จริงก่อนรับเป็น production artwork</p>
            </div>
            <label>
              หมายเหตุใหม่
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                maxLength={2000}
              />
            </label>
            <p className="muted">
              {pin
                ? `${displayValue(annotationProof?.content.label)} v${annotationProof?.version} · file ${displayValue(annotationProof?.content.fileId ?? 'unknown')} · หน้า ${displayValue(annotationProof?.content.proofPage ?? 1)} · x ${pin.x.toFixed(3)}, y ${pin.y.toFixed(3)}`
                : 'แตะบน proof เพื่อเลือกจุดหมายเหตุ'}
            </p>
            <button
              className="action secondary"
              disabled={
                !annotationProof ||
                !pin ||
                !comment ||
                busy ||
                (runtimeMode === 'connected' && !ready[annotationProof.id])
              }
              onClick={() =>
                annotationProof && pin && void onAnnotate(annotationProof, pin.x, pin.y, comment)
              }
            >
              บันทึกหมายเหตุ
            </button>
          </div>
          <div className="note-danger">
            การรับงานออกแบบไม่แทน technical หรือ regulatory clearance
            และไม่ทำให้พร้อมพิมพ์โดยอัตโนมัติ
          </div>
        </section>
      </div>
      <div className="footer-actions">
        <div>
          <strong>การตัดสินใจผูกกับ ฉบับที่ {version} เท่านั้น</strong>
          <p className="mock-note">ต้องมีสิทธิ์ด้านดีไซน์และรอผลยืนยันจากเซิร์ฟเวอร์</p>
        </div>
        <div className="actions">
          <button
            className="action secondary"
            disabled={
              !annotationProof ||
              !pin ||
              !comment ||
              busy ||
              (runtimeMode === 'connected' && !ready[annotationProof.id])
            }
            onClick={() =>
              annotationProof && pin && void onAnnotate(annotationProof, pin.x, pin.y, comment)
            }
          >
            ขอแก้ไข
          </button>
          <button
            className="action"
            disabled={
              !proof || busy || !canDecide || (runtimeMode === 'connected' && !ready[proof.id])
            }
            onClick={() => proof && void onDecision(proof)}
          >
            รับงานออกแบบ
            <Check />
          </button>
        </div>
      </div>
    </>
  );
}
function ExactProofMedia({
  proof,
  scale,
  onReady,
}: {
  readonly proof: OemRecord;
  readonly scale: number;
  readonly onReady: (id: string, ready: boolean) => void;
}) {
  const [url, setUrl] = useState(''),
    [mime, setMime] = useState(''),
    [error, setError] = useState('');
  useEffect(() => {
    let live = true;
    let local = '';
    if (typeof proof.content.fileId !== 'string') {
      return;
    }
    void fileBlob(proof.content.fileId)
      .then((blob) => {
        if (live) {
          local = URL.createObjectURL(blob);
          setUrl(local);
          setMime(blob.type);
          onReady(proof.id, true);
        }
      })
      .catch(() => {
        if (live) setError('ไฟล์ไม่พร้อมหรืออยู่นอกสิทธิ์');
      });
    return () => {
      live = false;
      if (local) URL.revokeObjectURL(local);
    };
  }, [proof.id, proof.content.fileId, onReady]);
  return url ? (
    <div className="art-sheet" style={{ transform: `scale(${scale})` }}>
      {mime === 'application/pdf' ? (
        <object
          aria-label={`Exact file ${displayValue(proof.content.fileId)} page ${displayValue(proof.content.proofPage ?? 1)}`}
          data={`${url}#page=${displayValue(proof.content.proofPage ?? 1)}`}
          type="application/pdf"
          width="100%"
          height="450"
          style={{ pointerEvents: 'none' }}
        />
      ) : (
        <img
          src={url}
          alt={`Exact file ${displayValue(proof.content.fileId)} page ${displayValue(proof.content.proofPage ?? 1)}`}
        />
      )}
    </div>
  ) : (
    <p className="note-warning">{error || 'กำลังโหลด exact file'}</p>
  );
}
